import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The design system's font sizes are utilities like `text-text-xxs` / `text-display-md`,
 * which stock tailwind-merge cannot tell from a text colour: `cn('text-text-xxs',
 * 'text-white')` would silently drop the size. They get their own class group that
 * conflicts only with other font sizes — deliberately not with `leading-*`, unlike the
 * built-in `font-size` group, so `cn('leading-none', 'text-text-xs')` keeps both exactly
 * as it did before the tokens were registered.
 *
 * Listed inline rather than imported from `tokens/font-size`: the shadcn registry ships
 * this file verbatim as `lib/utils.ts`, so it must stay self-contained. The unit spec
 * asserts the list matches the token module the Tailwind config consumes.
 */
export const CN_FONT_SIZE_TOKENS = [
  'display-2xl',
  'display-xl',
  'display-lg',
  'display-md',
  'display-sm',
  'display-xs',
  'text-xl',
  'text-lg',
  'text-md',
  'text-sm',
  'text-xs',
  'text-xxs',
] as const;

const twMerge = extendTailwindMerge<'font-size-token'>({
  extend: {
    classGroups: {
      'font-size-token': [{ text: [...CN_FONT_SIZE_TOKENS] }],
    },
    conflictingClassGroups: {
      'font-size-token': ['font-size'],
      'font-size': ['font-size-token'],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Creates a helper function for generating consistent test IDs.
 *
 * @param componentName - The name of the component (e.g., 'dashboard-header')
 * @returns A function that generates test IDs with the pattern: component-name-element-name
 *
 * @example
 * const testId = createTestIdHelper('dashboard-header');
 * testId('container') // Returns: "dashboard-header-container"
 * testId('title') // Returns: "dashboard-header-title"
 */
export function createTestIdHelper(componentName: string) {
  return (elementName: string) => `${componentName}-${elementName}`;
}
